import { buildQueryOpts } from '@supabase-cache-helpers/postgrest-react-query';
import type { PostgrestSingleResponse } from '@supabase/supabase-js';
import { useSuspenseInfiniteQuery as useReactQuerySuspenseInfiniteQuery } from '@tanstack/react-query';

interface UseSuspenseInfiniteQueryOptions {
  pageSize?: number;
  enabled?: boolean;
  refetchOnMount?: boolean;
  refetchOnWindowFocus?: boolean;
  staleTime?: number;
  retry?: number;
}

// Type for a Supabase query builder that supports range
type SupabaseQueryBuilder<T> = {
  range: (from: number, to: number) => SupabaseQueryBuilder<T>;
} & PromiseLike<PostgrestSingleResponse<T[]>>;

export function useSuspenseInfiniteQuery<T>(
  queryBuilder: SupabaseQueryBuilder<T>,
  options: UseSuspenseInfiniteQueryOptions = {},
) {
  const { pageSize = 10, ...restOptions } = options;

  // Use supabase-cache-helpers to build query options (for proper key generation and caching)
  const baseQueryOpts = buildQueryOpts(queryBuilder);

  const result = useReactQuerySuspenseInfiniteQuery({
    queryKey: [...baseQueryOpts.queryKey, 'infinite'],
    queryFn: async ({ pageParam = 0 }) => {
      const from = pageParam * pageSize;
      const to = from + pageSize - 1;

      // Create a new query with the range applied
      const response = await queryBuilder.range(from, to);

      // Simulate a network delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      if (response.error) {
        throw response.error;
      }

      return response.data;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage: T[], allPages: T[][]) => {
      // If the last page has the full page size, there might be more data
      return lastPage.length === pageSize ? allPages.length : undefined;
    },
    ...restOptions,
  });

  // Flatten all pages into a single array (like supabase-cache-helpers would do)
  const allData = result.data.pages.flatMap((page: T[]) => page);

  return {
    data: allData,
    isLoading: result.isLoading,
    isError: result.isError,
    error: result.error,
    fetchNextPage: result.fetchNextPage,
    hasNextPage: result.hasNextPage,
    isFetchingNextPage: result.isFetchingNextPage,
    refetch: result.refetch,
  };
}
